/* =========================================================================
   Audio (WebAudio, no external assets): tick, chime, buzz, applause, easter eggs.
   Typed port of the prototype synth — identical behavior. iOS WKWebView creates
   the context suspended; it must be resumed inside a user gesture.
   ========================================================================= */

let audioCtx: AudioContext | null = null;

type WindowWithWebkitAudio = Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext };

export function unlockAudio(): void {
  try {
    const w = window as WindowWithWebkitAudio;
    const Ctor = w.AudioContext || w.webkitAudioContext;
    if (!Ctor) return;
    audioCtx = audioCtx || new Ctor();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
  } catch {
    /* audio is a nicety, never a blocker */
  }
}

export function tone(freq: number, dur: number, type: OscillatorType = 'square', vol = 0.06, when = 0): void {
  if (!audioCtx) return;
  const o = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.value = vol;
  o.connect(g);
  g.connect(audioCtx.destination);
  const t = audioCtx.currentTime + when;
  o.start(t);
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.stop(t + dur);
}

export const sndTick = (): void => tone(880, 0.05, 'square', 0.025);
export const sndCorrect = (): void => {
  tone(660, 0.12, 'triangle', 0.08);
  tone(990, 0.18, 'triangle', 0.08, 0.11);
  tone(1320, 0.22, 'triangle', 0.06, 0.22);
};
export const sndWrong = (): void => tone(150, 0.4, 'sawtooth', 0.09);
export const sndJackpot = (): void => {
  [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, 0.25, 'triangle', 0.08, i * 0.09));
};

export function applause(): void {
  if (!audioCtx) return;
  const dur = 1.6,
    sr = audioCtx.sampleRate,
    len = Math.floor(sr * dur);
  const buf = audioCtx.createBuffer(1, len, sr),
    d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const env = Math.min(1, t / 0.15) * Math.max(0, 1 - (t - 0.15) / (dur - 0.15));
    const clap = Math.random() < 0.6 ? Math.random() * 2 - 1 : (Math.random() * 2 - 1) * 0.25;
    d[i] = clap * env;
  }
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1600;
  bp.Q.value = 0.6;
  const hp = audioCtx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 700;
  const g = audioCtx.createGain();
  g.gain.value = 0.5;
  src.connect(bp);
  bp.connect(hp);
  hp.connect(g);
  g.connect(audioCtx.destination);
  src.start();
}

/* filtered noise burst helper (for sneeze splat / coffee pour) */
export function noiseBurst(dur: number, freq: number, q: number, vol: number, when = 0, type: BiquadFilterType = 'bandpass'): void {
  if (!audioCtx) return;
  const sr = audioCtx.sampleRate,
    len = Math.floor(sr * dur);
  const buf = audioCtx.createBuffer(1, len, sr),
    d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const e = Math.min(1, i / (sr * 0.02)) * Math.max(0, 1 - i / len);
    d[i] = (Math.random() * 2 - 1) * e;
  }
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const f = audioCtx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = audioCtx.createGain();
  g.gain.value = vol;
  src.connect(f);
  f.connect(g);
  g.connect(audioCtx.destination);
  src.start(audioCtx.currentTime + when);
}

// A friendly "achoo!" — quick sniffle-up, then a gentle descending sneeze.
export function sndAchoo(): void {
  if (!audioCtx) return;
  tone(300, 0.1, 'sawtooth', 0.03, 0.0);
  tone(440, 0.1, 'sawtooth', 0.035, 0.1);
  const t = audioCtx.currentTime + 0.22;
  const o = audioCtx.createOscillator(),
    g = audioCtx.createGain();
  o.type = 'sawtooth';
  o.frequency.setValueAtTime(920, t);
  o.frequency.exponentialRampToValueAtTime(120, t + 0.34);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.09, t + 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
  o.connect(g);
  g.connect(audioCtx.destination);
  o.start(t);
  o.stop(t + 0.42);
  noiseBurst(0.3, 1200, 0.7, 0.07, 0.24);
}

// A gentle choir warmly harmonizing "Bless you" — soft major chord, resolving up.
export function sndChoir(): void {
  if (!audioCtx) return;
  const chord = [523.25, 659.25, 783.99, 1046.5]; // C E G C
  chord.forEach((f) => {
    const o = audioCtx!.createOscillator(),
      g = audioCtx!.createGain();
    o.type = 'sine';
    o.frequency.value = f;
    const t = audioCtx!.currentTime + 0.02;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.045, t + 0.16);
    g.gain.exponentialRampToValueAtTime(0.035, t + 0.55);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.15);
    o.connect(g);
    g.connect(audioCtx!.destination);
    o.start(t);
    o.stop(t + 1.2);
  });
}

// Satisfying pour — steady liquid stream with a couple of bubbly plinks.
export function sndPour(): void {
  if (!audioCtx) return;
  noiseBurst(0.7, 900, 1.2, 0.08, 0.0);
  tone(520, 0.1, 'sine', 0.05, 0.18);
  tone(430, 0.1, 'sine', 0.05, 0.34);
  tone(600, 0.1, 'sine', 0.045, 0.5);
}

// A soft angelic "ahhh" sigh — warm sustained chord, breathier than the choir.
export function sndAhh(): void {
  if (!audioCtx) return;
  const chord = [392.0, 523.25, 659.25]; // G C E
  chord.forEach((f) => {
    const o = audioCtx!.createOscillator(),
      g = audioCtx!.createGain();
    o.type = 'sine';
    o.frequency.value = f;
    const t = audioCtx!.currentTime + 0.04;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.04, t + 0.3);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.3);
    o.connect(g);
    g.connect(audioCtx!.destination);
    o.start(t);
    o.stop(t + 1.35);
  });
}

// A soft cartoon snore — one low wobbling "zzz".
export function sndSnore(): void {
  if (!audioCtx) return;
  const o = audioCtx.createOscillator(),
    g = audioCtx.createGain();
  o.type = 'sine';
  const t = audioCtx.currentTime;
  o.frequency.setValueAtTime(80, t);
  o.frequency.linearRampToValueAtTime(135, t + 0.32);
  o.frequency.linearRampToValueAtTime(68, t + 0.7);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.05, t + 0.26);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.72);
  o.connect(g);
  g.connect(audioCtx.destination);
  o.start(t);
  o.stop(t + 0.74);
}
